import { ProcessVariable, ProcessIncident } from './process';

export interface ProcessInstanceTimeline {
  processInstance: ProcessInstanceInfo;
  timeline: TimelineEvent[];
  statistics: TimelineStatistics;
}

export interface ProcessInstanceInfo {
  id: string;
  businessKey: string | null;
  processDefinitionId: string;
  processDefinitionKey: string;
  processDefinitionName: string;
  processDefinitionVersion: number;
  startTime: string; // ISO 8601
  endTime: string | null; // ISO 8601
  durationInMillis: number | null;
  state: "ACTIVE" | "SUSPENDED" | "COMPLETED" | "EXTERNALLY_TERMINATED" | "INTERNALLY_TERMINATED";
  startUserId: string | null;
  deleteReason: string | null;
  tenantId: string | null;
}

export interface TimelineEvent {
  id: string;
  timestamp: string; // ISO 8601
  type: TimelineEventType;
  category: TimelineCategory;
  activityId: string | null;
  activityName: string | null;
  activityType: string | null;
  executionId: string | null;
  taskId: string | null;
  duration: number | null; // em millisegundos
  status: EventStatus;
  details: EventDetails;
  metadata: EventMetadata;
}

export const TimelineEventType = {
  PROCESS_START: "process_start",
  PROCESS_END: "process_end",
  ACTIVITY_START: "activity_start",
  ACTIVITY_END: "activity_end",
  TASK_CREATED: "task_created",
  TASK_COMPLETED: "task_completed",
  VARIABLE_UPDATE: "variable_update",
  EXTERNAL_TASK_CREATED: "external_task_created",
  EXTERNAL_TASK_COMPLETED: "external_task_completed",
  EXTERNAL_TASK_FAILED: "external_task_failed",
  GATEWAY_PASSED: "gateway_passed",
  ERROR_OCCURRED: "error_occurred"
} as const;

export type TimelineEventType = typeof TimelineEventType[keyof typeof TimelineEventType];

export const TimelineCategory = {
  PROCESS: "process",
  ACTIVITY: "activity", 
  TASK: "task",
  VARIABLE: "variable",
  EXTERNAL_TASK: "external_task",
  GATEWAY: "gateway",
  ERROR: "error"
} as const;

export type TimelineCategory = typeof TimelineCategory[keyof typeof TimelineCategory];

export const EventStatus = {
  STARTED: "started",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
  ACTIVE: "active"
} as const;

export type EventStatus = typeof EventStatus[keyof typeof EventStatus];

export interface EventDetails {
  // Para atividades
  activityInstanceId?: string;
  parentActivityInstanceId?: string;
  canceled?: boolean;
  completeScope?: boolean;

  // Para tasks
  assignee?: string | null;
  owner?: string | null;
  priority?: number;
  taskDefinitionKey?: string;
  deleteReason?: string;

  // Para variáveis
  variableName?: string;
  variableType?: string;
  variableValue?: unknown;
  initial?: boolean;
  revision?: number;

  // Para external tasks
  topicName?: string;
  workerId?: string;
  retries?: number;
  errorMessage?: string;

  // Para erros
  errorDetails?: string;
  stackTrace?: string;
}

export interface EventMetadata {
  userOperationId?: string;
  tenantId?: string;
  removalTime?: string;
  rootProcessInstanceId?: string;
  
  // Dados derivados para facilitar o frontend
  isParallel?: boolean;
  branchId?: string; // Para identificar branches paralelos
  level?: number; // Nível de aninhamento
  hasChildren?: boolean;
  parentEventId?: string;
}

export interface TimelineStatistics {
  totalEvents: number;
  totalDuration: number; // em millisegundos
  eventsByType: Record<TimelineEventType, number>;
  eventsByCategory: Record<TimelineCategory, number>;
  parallelBranches: ParallelBranch[];
  criticalPath: CriticalPathInfo;
  performanceMetrics: PerformanceMetrics;
}

export interface ParallelBranch {
  id: string;
  startTime: string;
  endTime: string;
  duration: number;
  activities: string[]; // IDs das atividades
}

export interface CriticalPathInfo {
  totalDuration: number;
  events: string[]; // IDs dos eventos no caminho crítico
  bottlenecks: Bottleneck[];
}

export interface Bottleneck {
  eventId: string;
  activityName: string;
  duration: number;
  percentage: number; // % do tempo total
}

export interface PerformanceMetrics {
  averageActivityDuration: number;
  longestActivity: {
    eventId: string;
    activityName: string;
    duration: number;
  };
  shortestActivity: {
    eventId: string;
    activityName: string;
    duration: number;
  };
  waitingTime: number; // Tempo entre atividades
  processingTime: number; // Tempo efetivo de processamento
}

export interface TimelineQueryParams {
  // Filtros
  category?: TimelineCategory[];
  eventType?: TimelineEventType[];
  startTime?: string; // ISO 8601
  endTime?: string; // ISO 8601
  
  // Configurações de resposta
  includeVariables?: boolean; // default: false
  includeDetails?: boolean; // default: true
  includeStatistics?: boolean; // default: true
  
  // Paginação (para instâncias muito grandes)
  page?: number;
  limit?: number;
  
  // Ordenação
  sortBy?: "timestamp" | "duration" | "type";
  sortOrder?: "asc" | "desc";
}

// Tipos específicos para o componente BPMN
export interface BpmnTimelineProps {
  timeline: ProcessInstanceTimeline;
  bpmnXml: string;
  onEventClick?: (event: TimelineEvent) => void;
  onActivityClick?: (activityId: string) => void;
  selectedEventId?: string;
  showOnlyExecutedPath?: boolean;
  variables?: ProcessVariable[]; // Variáveis da instância do processo
  incidents?: ProcessIncident[]; // Incidentes da instância do processo
}

export interface ActivityExecutionInfo {
  activityId: string;
  executed: boolean;
  startTime?: string;
  endTime?: string;
  duration?: number;
  status: EventStatus;
  events: TimelineEvent[];
}
